import type { SafeAreaInsets } from "./safeAreaPolicy";
import {
  SAFE_AREA_CACHE_KEY,
  SAFE_AREA_CACHE_MAX_AGE_MS,
} from "./safeAreaPolicy";

export function isValidInsetNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v < 300;
}

export function readSafeAreaInsetsFromCSSVars(): SafeAreaInsets {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const root = document.documentElement;
  const style = getComputedStyle(root);
  return {
    top:
      parseInt(style.getPropertyValue("--safe-area-inset-top") || "0", 10) || 0,
    right:
      parseInt(style.getPropertyValue("--safe-area-inset-right") || "0", 10) ||
      0,
    bottom:
      parseInt(style.getPropertyValue("--safe-area-inset-bottom") || "0", 10) ||
      0,
    left:
      parseInt(style.getPropertyValue("--safe-area-inset-left") || "0", 10) ||
      0,
  };
}

export function readSafeAreaCache(): SafeAreaInsets | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SAFE_AREA_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as {
      top?: unknown;
      right?: unknown;
      bottom?: unknown;
      left?: unknown;
      ts?: unknown;
    };

    const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
    if (!ts || Date.now() - ts > SAFE_AREA_CACHE_MAX_AGE_MS) {
      return null;
    }

    const top = parsed.top;
    const right = parsed.right;
    const bottom = parsed.bottom;
    const left = parsed.left;
    if (
      !isValidInsetNumber(top) ||
      !isValidInsetNumber(right) ||
      !isValidInsetNumber(bottom) ||
      !isValidInsetNumber(left)
    ) {
      return null;
    }

    // 不接受全 0 缓存，避免“0 污染”导致启动后一直 0
    if (top === 0 && right === 0 && bottom === 0 && left === 0) {
      return null;
    }

    return { top, right, bottom, left };
  } catch {
    return null;
  }
}

export function writeSafeAreaCache(insets: SafeAreaInsets) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      SAFE_AREA_CACHE_KEY,
      JSON.stringify({ ...insets, ts: Date.now() }),
    );
  } catch {
    // ignore
  }
}

export function writeSafeAreaCSSVars(insets: SafeAreaInsets) {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  root.style.setProperty(
    "--safe-area-inset-top",
    `${Math.round(insets.top)}px`,
  );
  root.style.setProperty(
    "--safe-area-inset-right",
    `${Math.round(insets.right)}px`,
  );
  root.style.setProperty(
    "--safe-area-inset-bottom",
    `${Math.round(insets.bottom)}px`,
  );
  root.style.setProperty(
    "--safe-area-inset-left",
    `${Math.round(insets.left)}px`,
  );
}
